import { api } from "@/services/api"
import { kompresGambar } from "@/services/kompresGambar"

export interface EtalaseCategory {
  id: string
  name: string
  description: string
  sort_order: number
}

export interface EtalaseProduct {
  id: string
  slug: string
  name: string
  description: string
  price: number
  category_id: string
  /** nilai tersimpan: object key S3 (data/etalase/...), path statis, atau URL manual */
  image_url: string
  /** URL siap tampil (presigned GET utk object S3) — dibuat backend tiap respons */
  image_view_url?: string
  shopee_url: string
  is_active: boolean
  /** produk pilihan — dipajang di beranda */
  is_featured: boolean
  sort_order: number
  s3_status: "pending" | "synced" | "error"
  s3_error: string
  created_at: string
  updated_at: string
}

export const SUPLEMEN_CATEGORY_ID = "suplemen"

export function prioritizeEtalaseCategory(
  products: EtalaseProduct[],
  categoryId: string,
): EtalaseProduct[] {
  return [...products].sort((a, b) => {
    const aPriority = a.category_id === categoryId ? 0 : 1
    const bPriority = b.category_id === categoryId ? 0 : 1
    return aPriority - bPriority
  })
}

/** URL gambar siap tampil */
export function productImage(p: Pick<EtalaseProduct, "image_url" | "image_view_url">): string {
  return p.image_view_url || p.image_url
}

export function formatRupiah(price: number): string {
  return "Rp" + new Intl.NumberFormat("id-ID").format(price)
}

export function getEtalaseCategories(): Promise<EtalaseCategory[]> {
  return api.get<EtalaseCategory[]>("/etalase/categories")
}

export function getEtalaseProducts(
  opts: { category?: string; featuredOnly?: boolean } = {},
): Promise<{ items: EtalaseProduct[]; total: number }> {
  const qs = new URLSearchParams()
  if (opts.category) qs.set("category", opts.category)
  if (opts.featuredOnly) qs.set("featured", "true")
  const s = qs.toString()
  return api.get<{ items: EtalaseProduct[]; total: number }>(`/etalase${s ? `?${s}` : ""}`)
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export interface EtalaseProductInput {
  name: string
  description: string
  price: number
  category_id: string
  image_url: string
  shopee_url: string
  is_active: boolean
  is_featured?: boolean
  sort_order?: number
}

export function adminListEtalase(params: { search?: string; category?: string; page?: number; per_page?: number }) {
  const qs = new URLSearchParams()
  if (params.search) qs.set("search", params.search)
  if (params.category) qs.set("category", params.category)
  if (params.page) qs.set("page", String(params.page))
  if (params.per_page) qs.set("per_page", String(params.per_page))
  const s = qs.toString()
  return api.get<{ items: EtalaseProduct[]; total: number }>(`/admin/etalase${s ? `?${s}` : ""}`)
}

export function adminCreateEtalase(input: EtalaseProductInput) {
  return api.post<EtalaseProduct>("/admin/etalase", input)
}

export function adminUpdateEtalase(id: string, input: EtalaseProductInput) {
  return api.patch<EtalaseProduct>(`/admin/etalase/${id}`, input)
}

export function adminDeleteEtalase(id: string) {
  return api.delete<void>(`/admin/etalase/${id}`)
}

export function adminEtalaseStats() {
  return api.get<{ total_products: number }>("/admin/etalase/stats")
}

interface PresignUploadResult {
  upload_url: string
  object_key: string
  view_url: string
  expires_in_seconds: number
}

// Upload gambar via presigned URL (pola apetorium, bucket privat):
// minta URL bertanda tangan dari backend → PUT file LANGSUNG ke S3.
// Yang DISIMPAN di DB adalah object_key; view_url (presigned GET) hanya
// untuk preview — backend membuatkan yang segar di setiap respons.
export async function adminUploadEtalaseImage(file: File): Promise<{ key: string; viewUrl: string }> {
  // Perkecil dulu: foto 1,4 MB dari kamera/ponsel tidak ada gunanya di kartu
  // produk selebar 160 piksel, dan jadi muatan terberat halaman publik.
  const { file: siap } = await kompresGambar(file)
  file = siap

  const presign = await api.post<PresignUploadResult>("/admin/etalase/upload-url", {
    filename: file.name,
    content_type: file.type,
  })

  const res = await fetch(presign.upload_url, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  })
  if (!res.ok) throw new Error(`Upload ke S3 gagal (${res.status})`)

  return { key: presign.object_key, viewUrl: presign.view_url }
}
